import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import seedrandom from 'seedrandom';

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function defaultSize() {
  return { width: 820, height: 460 };
}

function makePipeRng(seed, pipeIndex) {
  return seedrandom(`${String(seed ?? '0')}-pipe-${pipeIndex}`);
}

function computeDifficulty(frame, fixedDtMs) {
  const seconds = Math.max(0, (frame * fixedDtMs) / 1000);
  // Deterministic difficulty curve (time-based): smooth, capped.
  const speedMultiplier = clamp(1 + seconds * 0.03, 1, 3);
  const gapSize = clamp(180 - Math.min(seconds * 4, 100), 80, 180);
  return { speedMultiplier, gapSize };
}

function generatePipe({ seed, pipeIndex, x, height, gapMargin, groundHeight, frame, fixedDtMs }) {
  const rng = makePipeRng(seed, pipeIndex);
  const { gapSize } = computeDifficulty(frame, fixedDtMs);
  const playableHeight = Math.max(0, height - groundHeight);

  const minY = gapMargin + gapSize / 2;
  const maxY = playableHeight - gapMargin - gapSize / 2;
  const gapY = (maxY > minY) ? (minY + rng() * (maxY - minY)) : (playableHeight / 2);

  return {
    id: `pipe-${pipeIndex}`,
    x,
    gapY,
    gapSize,
    passedA: false,
    passedB: false,
  };
}

export default function FlappySimCanvas({
  seed,
  socket,
  matchId,
  startTimeMs,
  durationSeconds = 60,
  localPlayerLabel = 'YOU',
  opponentPlayerLabel = 'OPPONENT',
  disabled = false,
  onFinish,
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  const [connectionQuality, setConnectionQuality] = useState('good');
  const [status, setStatus] = useState('waiting'); // waiting | countdown | running | ended

  const config = useMemo(() => {
    const { width, height } = defaultSize();
    return {
      width,
      height,
      groundHeight: 40,
      birdX: 170,
      birdRadius: 12,
      gravity: 1050,
      flapVel: -330,
      pipeWidth: 70,
      gapMargin: 90,
      baseScrollSpeed: 170, // px/s
      spacing: 260,
      maxCatchupStepsPerFrame: 12,
    };
  }, []);

  const stateRef = useRef({
    started: false,
    frame: 0,
    pipes: [],
    pipeIndex: 0,

    playerA: { y: 230, v: 0, score: 0, alive: true },
    playerB: { y: 230, v: 0, score: 0, alive: true },

    inputsA: new Map(), // frame -> count
    inputsB: new Map(),

    lastNowMs: 0,
    endedReasonA: null,
    endedReasonB: null,

    flapTimesA: [],
    finishedA: false,
  });

  const reset = useCallback(() => {
    const s = stateRef.current;
    s.started = false;
    s.frame = 0;
    s.pipes = [];
    s.pipeIndex = 0;
    s.playerA = { y: 230, v: 0, score: 0, alive: true };
    s.playerB = { y: 230, v: 0, score: 0, alive: true };
    s.inputsA = new Map();
    s.inputsB = new Map();
    s.endedReasonA = null;
    s.endedReasonB = null;
    s.flapTimesA = [];
    s.finishedA = false;
  }, []);

  // Apply opponent inputs from socket
  useEffect(() => {
    if (!socket) return undefined;

    const onOpponentInput = (payload) => {
      if (!payload || String(payload.matchId) !== String(matchId)) return;
      const frame = Number(payload.frame);
      if (!Number.isFinite(frame) || frame < 0) return;

      const s = stateRef.current;
      const cur = s.inputsB.get(frame) || 0;
      s.inputsB.set(frame, cur + 1);

      if (typeof payload.rttMs === 'number') {
        const rtt = payload.rttMs;
        if (rtt > 300) setConnectionQuality('poor');
        else if (rtt > 150) setConnectionQuality('ok');
        else setConnectionQuality('good');
      }
    };

    socket.on('flappy:sim:opponent-input', onOpponentInput);
    return () => {
      socket.off('flappy:sim:opponent-input', onOpponentInput);
    };
  }, [matchId, socket]);

  // Local input
  const flapLocal = useCallback(() => {
    if (disabled) return;
    const s = stateRef.current;
    if (!s.started) return;
    if (!s.playerA.alive) return;

    const frame = s.frame;
    const cur = s.inputsA.get(frame) || 0;
    s.inputsA.set(frame, cur + 1);

    // Apply locally immediately for responsiveness
    s.playerA.v = config.flapVel;

    // Record flap times (ms since start) for server storage / replay.
    // Use the sim frame index for determinism.
    const tMs = Math.round((s.frame * (1000 / 60)));
    const maxMs = Math.max(0, Math.round(Number(durationSeconds || 60) * 1000));
    if (Number.isFinite(tMs) && tMs >= 0 && tMs <= maxMs) {
      s.flapTimesA.push(tMs);
      if (s.flapTimesA.length > 5000) s.flapTimesA.length = 5000;
    }

    if (socket) {
      const t0 = Date.now();
      socket.emit('flappy:sim:input', {
        matchId,
        frame,
        type: 'FLAP',
        clientTime: t0,
      });
    }
  }, [config.flapVel, disabled, durationSeconds, matchId, socket]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp' || e.key === ' ' || e.key === 'ArrowUp' || e.key === 'w' || e.key === 'W') {
        e.preventDefault();
        flapLocal();
      }
    };
    window.addEventListener('keydown', onKeyDown, { passive: false });
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [flapLocal]);

  // Start countdown / running based on startTimeMs
  useEffect(() => {
    reset();
    if (!startTimeMs) {
      setStatus('waiting');
      return;
    }

    const now = Date.now();
    if (now < startTimeMs) setStatus('countdown');
    else setStatus('running');
  }, [reset, startTimeMs]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const fixedDtMs = 1000 / 60;
    const durationMs = Math.max(0, Math.round(Number(durationSeconds || 60) * 1000));

    const ensurePipes = (s, frame) => {
      while (s.pipes.length < 12) {
        const lastX = s.pipes.length ? s.pipes[s.pipes.length - 1].x : (config.width + 60);
        const x = s.pipes.length ? (lastX + config.spacing) : lastX;
        s.pipes.push(
          generatePipe({
            seed,
            pipeIndex: s.pipeIndex,
            x,
            height: config.height,
            gapMargin: config.gapMargin,
            groundHeight: config.groundHeight,
            frame,
            fixedDtMs,
          }),
        );
        s.pipeIndex += 1;
      }
    };

    const stepOne = (s) => {
      const { speedMultiplier } = computeDifficulty(s.frame, fixedDtMs);
      const scrollSpeed = config.baseScrollSpeed * speedMultiplier;
      const dt = fixedDtMs / 1000;

      // Apply buffered flaps for this frame
      const aFlaps = s.inputsA.get(s.frame) || 0;
      const bFlaps = s.inputsB.get(s.frame) || 0;
      if (aFlaps > 0 && s.playerA.alive) s.playerA.v = config.flapVel;
      if (bFlaps > 0 && s.playerB.alive) s.playerB.v = config.flapVel;

      // Physics
      if (s.playerA.alive) {
        s.playerA.v += config.gravity * dt;
        s.playerA.y += s.playerA.v * dt;
      }
      if (s.playerB.alive) {
        s.playerB.v += config.gravity * dt;
        s.playerB.y += s.playerB.v * dt;
      }

      // Move pipes
      s.pipes.forEach((p) => { p.x -= scrollSpeed * dt; });

      // Cleanup
      s.pipes = s.pipes.filter((p) => (p.x + config.pipeWidth) > -120);

      // Spawn ahead
      ensurePipes(s, s.frame);

      // Score
      s.pipes.forEach((p) => {
        if (!p.passedA && p.x + config.pipeWidth < config.birdX - config.birdRadius) {
          p.passedA = true;
          if (s.playerA.alive) s.playerA.score += 1;
        }
        if (!p.passedB && p.x + config.pipeWidth < config.birdX - config.birdRadius) {
          p.passedB = true;
          if (s.playerB.alive) s.playerB.score += 1;
        }
      });

      // Collisions helper
      const checkHit = (birdY) => {
        const hitTop = birdY - config.birdRadius < 0;
        const hitBottom = birdY + config.birdRadius > config.height - config.groundHeight;
        if (hitTop || hitBottom) return true;

        for (const p of s.pipes) {
          const withinX = (config.birdX + config.birdRadius) > p.x && (config.birdX - config.birdRadius) < (p.x + config.pipeWidth);
          if (!withinX) continue;

          const gapTop = p.gapY - p.gapSize / 2;
          const gapBottom = p.gapY + p.gapSize / 2;
          const withinGap = (birdY - config.birdRadius) >= gapTop && (birdY + config.birdRadius) <= gapBottom;
          if (!withinGap) return true;
        }

        return false;
      };

      if (s.playerA.alive && checkHit(s.playerA.y)) {
        s.playerA.alive = false;
        s.endedReasonA = 'crash';
      }
      if (s.playerB.alive && checkHit(s.playerB.y)) {
        s.playerB.alive = false;
        s.endedReasonB = 'crash';
      }

      s.frame += 1;

      if (!s.playerA.alive && !s.playerB.alive) setStatus('ended');
    };

    const draw = () => {
      const s = stateRef.current;
      const { width, height } = config;

      const { speedMultiplier, gapSize } = computeDifficulty(s.frame, fixedDtMs);
      const scrollSpeed = config.baseScrollSpeed * speedMultiplier;

      // Background
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#0b1320';
      ctx.fillRect(0, 0, width, height);

      // Ground
      ctx.fillStyle = '#101b2d';
      ctx.fillRect(0, height - config.groundHeight, width, config.groundHeight);

      // Pipes
      ctx.fillStyle = '#1faa59';
      s.pipes.forEach((p) => {
        const topH = p.gapY - p.gapSize / 2;
        const bottomY = p.gapY + p.gapSize / 2;
        ctx.fillRect(p.x, 0, config.pipeWidth, topH);
        ctx.fillRect(p.x, bottomY, config.pipeWidth, height - bottomY - config.groundHeight);
      });

      // Birds
      // Player A
      ctx.globalAlpha = s.playerA.alive ? 1 : 0.35;
      ctx.fillStyle = '#ffb703';
      ctx.beginPath();
      ctx.arc(config.birdX, s.playerA.y, config.birdRadius, 0, Math.PI * 2);
      ctx.fill();

      // Player B (slightly offset X so both visible)
      ctx.globalAlpha = s.playerB.alive ? 1 : 0.35;
      ctx.fillStyle = '#4cc9f0';
      ctx.beginPath();
      ctx.arc(config.birdX + 28, s.playerB.y, config.birdRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;

      // HUD
      ctx.fillStyle = '#e9ecef';
      ctx.font = '16px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
      ctx.fillText(`${localPlayerLabel}: ${s.playerA.score}`, 16, 26);
      ctx.fillText(`${opponentPlayerLabel}: ${s.playerB.score}`, 16, 48);
      ctx.fillText(`Speed: ${Math.round(scrollSpeed)}`, 16, 70);
      ctx.fillText(`Gap: ${Math.round(gapSize)}`, 16, 92);
      ctx.fillText(`Net: ${connectionQuality}`, 16, 114);

      if (status === 'countdown' && startTimeMs) {
        const leftMs = Math.max(0, startTimeMs - Date.now());
        const left = Math.ceil(leftMs / 1000);
        ctx.fillStyle = 'rgba(0,0,0,0.5)';
        ctx.fillRect(0, 0, width, height);
        ctx.fillStyle = '#e9ecef';
        ctx.font = '46px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(left <= 0 ? 'GO!' : String(left), width / 2, height / 2);
        ctx.textAlign = 'left';
      }

      if (status === 'waiting') {
        ctx.fillStyle = 'rgba(0,0,0,0.45)';
        ctx.fillRect(0, 0, width, height);
        ctx.fillStyle = '#e9ecef';
        ctx.font = '16px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Waiting for match start…', width / 2, height / 2);
        ctx.textAlign = 'left';
      }

      if (status === 'ended') {
        ctx.fillStyle = 'rgba(0,0,0,0.45)';
        ctx.fillRect(0, 0, width, height);
        ctx.fillStyle = '#e9ecef';
        ctx.font = '22px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
        ctx.textAlign = 'center';
        const a = s.playerA.score;
        const b = s.playerB.score;
        const res = a === b ? 'TIE' : (a > b ? 'YOU WIN' : 'YOU LOSE');
        ctx.fillText(res, width / 2, height / 2);
        ctx.textAlign = 'left';
      }
    };

    const loop = () => {
      const s = stateRef.current;

      if (!startTimeMs) {
        s.started = false;
        setStatus('waiting');
      } else {
        const now = Date.now();
        if (now < startTimeMs) {
          s.started = false;
          setStatus('countdown');
        } else if (!s.started) {
          s.started = true;
          s.frame = 0;
          s.pipes = [];
          s.pipeIndex = 0;
          s.inputsA = new Map();
          s.inputsB = new Map();
          setStatus('running');
        }
      }

      if (s.started && status === 'running') {
        const elapsedMs = Math.max(0, Date.now() - startTimeMs);

        // Time limit end condition
        if (durationMs > 0 && elapsedMs >= durationMs) {
          if (s.playerA.alive) {
            s.playerA.alive = false;
            s.endedReasonA = 'time';
          }
          if (s.playerB.alive) {
            s.playerB.alive = false;
            s.endedReasonB = 'time';
          }
          setStatus('ended');
        }

        const targetFrame = Math.floor(elapsedMs / fixedDtMs);
        let steps = 0;
        while (s.frame < targetFrame && steps < config.maxCatchupStepsPerFrame) {
          stepOne(s);
          steps += 1;
        }
      }

      // Fire onFinish once when local run ends
      if (!s.finishedA && !s.playerA.alive && s.endedReasonA) {
        s.finishedA = true;
        const elapsedMs = startTimeMs ? Math.max(0, Date.now() - startTimeMs) : 0;
        const safeEndedAtMs = durationMs > 0 ? Math.min(elapsedMs, durationMs) : elapsedMs;
        const payload = {
          score: s.playerA.score,
          endedReason: s.endedReasonA,
          endedAtMs: Math.round(safeEndedAtMs),
          flapTimes: s.flapTimesA,
        };
        if (typeof onFinish === 'function') {
          try {
            onFinish(payload);
          } catch (e) {
            // ignore
          }
        }
      }

      draw();
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [config, connectionQuality, disabled, durationSeconds, localPlayerLabel, opponentPlayerLabel, onFinish, seed, startTimeMs, status]);

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={config.width}
        height={config.height}
        style={{
          width: '100%',
          borderRadius: 12,
          border: '1px solid rgba(255,255,255,0.08)',
          display: 'block',
          background: '#0b1320',
          touchAction: 'none',
          WebkitTapHighlightColor: 'transparent',
          userSelect: 'none',
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          flapLocal();
        }}
        onTouchStart={(e) => {
          e.preventDefault();
          flapLocal();
        }}
      />

      <div style={{ marginTop: 8, color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>
        Controls: Space / ↑ / W / Click / Tap
      </div>
    </div>
  );
}
